import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Container, FormControl, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import { GenericDrawer } from 'app/components/GenericDrawer/GenericDrawer';
import _ from 'lodash';

import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import GrowingTable from 'app/components/GrowingTable';
import VehicleForm from 'app/components/VehicleForm';

import { setDeletedVehicles, setVehicleByIndex, setVehicles } from 'app/store/slices/forms';

import helpers from 'app/utils/helpers';
import notification, { closeToast, toastIds } from 'app/config/notification';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import surveyEndpoint from 'app/api/services/surveyEndpoint';
import withVehicleInitialization from 'app/components/Hoc/withVehicleInitialization';

import { SERVICES_CODES } from 'app/config/services';
import { STATUS_CODES } from 'app/config/status';
import { errorHandler } from 'app/utils/errorHandler';
import { vehiclePayload } from 'app/utils/forms/payloadsStruture';
import { preparedFormData, validateDocumentsDescription } from 'app/utils/forms/functions';
import { validateVehicleAge } from 'app/utils/forms/validators/vehicle';

import styles from './styles';
import { cancelDAE, daeIsPaid } from 'app/store/slices/daes';
import { EXTERNAL_CONTEXT } from 'app/config/urls';
import { updateRequestDetails } from 'app/store/slices/requests';
import { MAX_VEHICLES_REQUEST } from 'app/utils/constants';

const useStyles = makeStyles(styles);

function TableEdit(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        intl,
        initialLoading,
        setInitialLoading,
        openDrawer,
        setOpenDrawer,
        vehicleSelected,
        setVehicleSelected,
        reset,
        onCancel,
        onCloseDrawer,
        contextProps: { isInternalForm, canEdit, onCancel: onCancelRequest, loadingPlates },
    } = props;

    const vehicles = useSelector((state) => state.forms.vehicles);
    const deletedVehicles = useSelector((state) => state.forms.deletedVehicles);
    const vehiclePlates = useSelector((state) => state.vehicle.vehiclePlate);
    //const vehicleAllPlates = useSelector(state => state.vehicle.vehicleAllPlates);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const subSystems = useSelector((state) => state.vehicle.subSystems);
    const userLogged = useSelector((state) => state.auth.user);
    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const otherDocuments = useSelector((state) => state.forms.files);

    const [canPartialEdit] = useState(() => {
        if (isInternalForm) return false;
        if (request.cdSituacao === STATUS_CODES.AGUARDANDO_PAGAMENTO && !isInternalForm) {
            return true;
        }
        return canEdit;
    });

    const [rows, setRows] = useState([]);
    const [plates, setPlates] = useState([]);
    const [loadingVehicleData, setLoadingVehicleData] = useState(false);
    const [maxVehicles, setMaxVehicles] = useState(MAX_VEHICLES_REQUEST);

    const [canShowEditButton] = useState(() => {
        if (!canEdit) {
            if (service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS) {
                if (!request.situacao) return true; // new request
                return request.situacao?.boEdicaoPortalExterno || request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;
            }
        }
        return canEdit;
    });

    useEffect(() => {
        if (service.boGeraDae) {
            if (request.cdSituacao > STATUS_CODES.EM_ANALISE && vehicles.length > 0) {
                if (maxVehicles === MAX_VEHICLES_REQUEST) setMaxVehicles(vehicles.length);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles]);

    const populateTableData = useCallback(
        (tempTableData) => {
            const tempInt = { ...tempTableData };
            if (vehicleType.length !== 0 && subSystems.length !== 0) {
                tempInt.data = vehicles.map((v) => {
                    const temp = { ...v };
                    if (temp.cdSubSistema) {
                        const found = _.find(subSystems, ['cdSubSistema', Number(v.cdSubSistema)]);
                        if (found && found.deSubSistema) {
                            temp.deSubSistema = found.deSubSistema;
                        }
                    }
                    if (temp.documentos && temp.documentos.length > 0) {
                        temp.isOk = true;
                    }
                    temp.showEditButton = true;
                    temp.showDeleteButton = canPartialEdit;
                    if (!temp.dePlaca || temp.dePlaca === '' || !temp.cdVeiculo || !temp.cdVeiculo === '') {
                        temp.showEditButton = false;
                    }
                    return temp;
                });
            }
            return tempInt;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [subSystems, vehicleType.length, vehicles],
    );

    useEffect(() => {
        const init = () => {
            let tempTableData = { ...tableData };
            tempTableData = populateTableData(tempTableData);
            setTableData(tempTableData);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [populateTableData, subSystems, vehicleType, vehicles]);

    useEffect(() => {
        const temp = vehicles.map((v) => {
            const t = _.cloneDeep(v);
            t.showEditButton = true;
            t.showDeleteButton = canPartialEdit;
            if (!t.dePlaca || t.dePlaca === '' || !t.cdVeiculo || !t.cdVeiculo === '') {
                t.showEditButton = false;
            }
            if (t.documentos && t.documentos.length > 0) {
                t.isOk = true;
            }
            return t;
        });
        setRows(temp);
    }, [canEdit, canPartialEdit, request.cdSituacao, service.cdServico, vehicles]);

    const handleChangePlate = useCallback(
        async (event, vehicleIndex) => {
            setLoadingVehicleData(true);
            try {
                dispatch(
                    setVehicleByIndex({
                        data: {
                            dePlaca: <CircularProgress size={16} />,
                            deRenavam: 'Carregando...',
                        },
                        vehicleIndex,
                    }),
                );
                const plate = event.target.value;
                let indexPlate = null;
                plates.forEach((o, i) => {
                    if (o === plate) {
                        indexPlate = i;
                    }
                });

                const response = await vehiclesEndpoint.getVehiclesByPlate(
                    plate,
                    internalNewRequest?.company?.cdEmpresa && internalNewRequest.company.cdEmpresa,
                );

                if (response.status === 200 && response.data) {
                    const dataResponse = response.data;
                    dataResponse.documentos = [];
                    dataResponse.showEditButton = true;
                    dataResponse.hideDeleteButton = false;

                    if (dataResponse.cdSubSistema) {
                        const found = _.find(subSystems, ['cdSubSistema', Number(dataResponse.cdSubSistema)]);
                        if (found && found.deSubSistema) {
                            dataResponse.deSubSistema = found.deSubSistema;
                        }
                    }

                    dispatch(setVehicleByIndex({ data: dataResponse, vehicleIndex }));
                    if (indexPlate != null) {
                        const tempPlates = _.orderBy(plates);
                        const temp = [...tempPlates];
                        temp.splice(indexPlate, 1);
                        setPlates(temp);
                    }
                } else {
                    const response = {};
                    response.status = 500;
                    response.data = {
                        message: 'Problemas ao recuperar dados do veículo.',
                    };
                    throw Object({
                        response: {
                            status: 500,
                            data: {
                                message: 'Problemas ao recuperar dados do veículo.',
                            },
                        },
                    });
                }
            } catch (error) {
                console.error({ error });
                dispatch(setVehicleByIndex({ data: {}, vehicleIndex }));
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            setLoadingVehicleData(false);
        },
        [internalNewRequest, dispatch, plates, subSystems],
    );

    const initialTableData = {
        columns: [
            {
                title: 'ID',
                field: 'cdVeiculo',
                align: 'left',
                showContent: (value) => {
                    return <div>{value.cdVeiculo || '-'}</div>;
                },
            },
            {
                title: 'Placa',
                field: 'dePlaca',
                align: 'left',
                showContent: (value, index) => {
                    return (
                        <>
                            {value?.dePlaca && value.dePlaca !== '' && <>{value.dePlaca}</>}
                            {(!value || value.dePlaca === '') && (
                                <FormControl className={classes.selectPlate}>
                                    <InputLabel id={`placa${index}`}>Selecione</InputLabel>
                                    <Select
                                        value={value.dePlaca}
                                        labelId={`placa${index}`}
                                        onChange={(event) => handleChangePlate(event, index)}>
                                        <MenuItem value="">
                                            <em></em>
                                        </MenuItem>
                                        {plates.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    );
                },
            },
            {
                title: 'Renavam',
                field: 'deRenavam',
                align: 'left',
                showContent: (value) => {
                    return <div>{value.deRenavam}</div>;
                },
            },
            {
                title: 'n Ordem Empresa',
                field: 'nuOrdemEmpresa',
                align: 'left',
                showContent: (value) => {
                    return <div>{value.nuOrdemEmpresa}</div>;
                },
            },
            {
                title: 'Utilização',
                field: 'deUtilizacao',
                align: 'left',
                showContent: (value) => {
                    return <div>{value.deUtilizacao}</div>;
                },
            },
            {
                title: 'Subsistema',
                field: 'deSubSistema',
                align: 'left',
                showContent: (value) => {
                    return <div>{value?.deSubSistema}</div>;
                },
            },
        ],
        data: rows,
    };

    const [tableData, setTableData] = useState(initialTableData);

    useEffect(() => {
        if (vehiclePlates.length > 0) {
            let temp = [...vehiclePlates];
            if (vehicles.length > 0) {
                const platesTemp = vehicles.map((v) => v.dePlaca);
                temp = temp.filter((p) => !platesTemp.includes(p));
            }
            temp = _.orderBy(temp);
            setPlates(temp);
        }
    }, [vehiclePlates, vehicles]);

    useEffect(() => {
        if (plates.length > 0) {
            let tempTableData = { ...initialTableData };
            tempTableData = populateTableData(tempTableData);
            setTableData(tempTableData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [populateTableData, plates]);

    const addElement = (vehi) => {
        setTableData(vehi);
        // setData(contact.data);
    };

    const onChangeField = () => {};

    const onSaveRow = async (values, index, canEditField) => {
        setInitialLoading(true);
        if (!values.documentos || values.documentos.length === 0) {
            notification(intl.formatMessage({ id: 'MESSAGES.FORMS.DOCUMENT_REQUIRED' }), {
                toastId: 'MESSAGES.FORMS.DOCUMENT_REQUIRED',
                type: 'error',
            });
            setInitialLoading(false);
            return;
        }

        const valuesInt = { ...values };

        if (valuesInt.deUtilizacao === 'Sistema' && service.cdServico !== SERVICES_CODES.EXCLUSAO_VEICULOS) {
            const subSystem = _.find(subSystems, ['cdSubSistema', Number(valuesInt.cdSubSistema)]);
            const maxAge = await validateVehicleAge({
                service: service,
                plate: values.dePlaca,
                subSystem: canEditField ? subSystem : null,
                chassiYear: canEditField ? valuesInt.nuAnoFabricacao : null,
                deUtilizacao: canEditField ? valuesInt.deUtilizacao : null,
            });
            if (subSystem && maxAge) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_MAX_AGE' }), {
                    toastId: 'MESSAGES.SERVICE.VEHICLE_MAX_AGE',
                    type: 'error',
                });
                setInitialLoading(false);
                return;
            }
        }

        if (internalNewRequest?.cdUsuario) {
            valuesInt.cdUsuario = internalNewRequest.cdUsuario;
        } else {
            if (!isInternalForm) {
                valuesInt.cdUsuario =
                    userLogged.cdUsuarioAdmin && userLogged.cdUsuarioAdmin !== ''
                        ? userLogged.cdUsuarioAdmin
                        : userLogged.username;
            }
        }

        const docsExtrasValidation = validateDocumentsDescription(otherDocuments);
        if (!docsExtrasValidation && !isInternalForm) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.DOCUMENT_DESCRIPTION_REQUIRED' }), {
                toastId: 'MESSAGES.SERVICE.FORMS.DOCUMENT_DESCRIPTION_REQUIRED',
                type: 'error',
            });
            setInitialLoading(false);
            return;
        }

        if (otherDocuments && otherDocuments.length > 0) {
            const documentsValues = _.cloneDeep(valuesInt.documentos);
            otherDocuments.forEach((doc) => {
                let key = _.findIndex(valuesInt.documentos, (d) => {
                    return d.idFile === doc.idFile;
                });

                if (key < 0) {
                    documentsValues.push(doc);
                } else {
                    documentsValues[key] = doc;
                }
            });
            valuesInt.documentos = documentsValues;
        }

        if (valuesInt.deCpfCnpjProprietario) {
            valuesInt.deCpfCnpjProprietario = helpers.somenteAlfanumericos(valuesInt.deCpfCnpjProprietario);
        }

        const temp = _.cloneDeep(vehicles);
        valuesInt.edited = true;
        if (index != null) {
            temp[index] = valuesInt;
        } else {
            temp.push(valuesInt);
        }

        if (temp.documentos && temp.documentos.length > 0) {
            temp.isOk = true;
        }

        temp.showEditButton = canEdit;
        temp.hideDeleteButton = !canShowEditButton;

        const tableDt = { ...tableData };
        tableDt.data = temp;
        setTableData(tableDt);

        await dispatch(setVehicles(temp));
        reset();
        setInitialLoading(false);
    };

    const customEditElement = (element, index) => {
        setVehicleSelected({
            index,
            content: element,
        });
        setOpenDrawer(true);
    };

    const changeVehicles = async (index) => {
        if (loadingVehicleData) return;

        if (index === undefined) {
            if (vehicles.length === vehiclePlates.length) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.MAX_VEHICLES' }), {
                    toastId: 'MESSAGES.SERVICE.MAX_VEHICLES',
                    type: 'warning',
                });
                return;
            }
            const temp = _.cloneDeep(vehicles);
            const objPayload = { ...vehiclePayload };
            objPayload.showEditButton = false;
            objPayload.hideDeleteButton = true;
            temp.push(objPayload);
            setInitialLoading(true);
            await dispatch(setVehicles(temp));
            setInitialLoading(false);
        } else {
            const temp = [...vehicles];
            const delVehicles = [...deletedVehicles];
            if (window.confirm('Deseja excluir este item?')) {
                setInitialLoading(true);

                if (request.cdSolicitacao) {
                    const isPaid = await daeIsPaid(request.cdSolicitacao, temp[index].dePlaca, intl);
                    if (isPaid) {
                        setInitialLoading(false);
                        return;
                    }

                    if (request.cdSituacao === STATUS_CODES.AGUARDANDO_PAGAMENTO && temp.length === 1) {
                        if (window.confirm('Ao excluir este item, a solicitação será cancelada. Deseja continuar?')) {
                            onCancelRequest();
                        } else {
                            notification(
                                intl.formatMessage({
                                    id: 'MESSAGES.SERVICE.FORMS.DELETE_VEHICLE_NOT_ALLOWED_EMPTY',
                                }),
                                {
                                    toastId: 'MESSAGES.SERVICE.FORMS.DELETE_VEHICLE_NOT_ALLOWED_EMPTY',
                                    type: 'error',
                                },
                            );
                        }
                        setInitialLoading(false);
                        return;
                    }

                    if (service.cdServico === SERVICES_CODES.VISTORIA) {
                        const cdVeiculo = temp[index]?.cdVeiculo;
                        if (cdVeiculo && request?.jsonSolicitacao) {
                            let json = JSON.parse(request.jsonSolicitacao);
                            if (json.vistoriaVeiculos && json.vistoriaVeiculos.length > 0) {
                                const found = _.find(json.vistoriaVeiculos, ['cdVeiculo', cdVeiculo]);
                                let cdVistoriaVeiculo = null;
                                if (found) {
                                    cdVistoriaVeiculo = found.cdVistoriaVeiculo;
                                }
                                try {
                                    if (cdVistoriaVeiculo) {
                                        const canceled = await cancelDAE(request.cdSolicitacao, [temp[index]]);
                                        if (!canceled) {
                                            throw String(
                                                'Problema ao cancelar DAE gerada para este veículo. Tente novamente.',
                                            );
                                        }
                                        const response = await surveyEndpoint.deleteVehicle(
                                            cdVistoriaVeiculo,
                                            request.cdSolicitacao,
                                        );
                                        if (!response?.status || response.status !== 200) {
                                            throw intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_DELETE_ERROR' });
                                        } else {
                                            //buscar na solicitação e reescrever
                                            const tempRequest = _.cloneDeep(request);
                                            const tempVistoriaVeiculos = json.vistoriaVeiculos;
                                            let key = _.findIndex(tempVistoriaVeiculos, ['cdVeiculo', cdVeiculo]);
                                            if (key >= 0) {
                                                tempVistoriaVeiculos.splice(key, 1);
                                                json.vistoriaVeiculos = tempVistoriaVeiculos;
                                                json = JSON.stringify(json);
                                                tempRequest.jsonSolicitacao = json;
                                                await surveyEndpoint.saveRequest(
                                                    preparedFormData({ payload: tempRequest }),
                                                    EXTERNAL_CONTEXT,
                                                );
                                                await dispatch(updateRequestDetails(tempRequest));
                                            }
                                        }
                                    }
                                } catch (error) {
                                    notification(error, {
                                        toastId: 'SERVER_ERROR_INT',
                                        type: 'error',
                                    });
                                    setInitialLoading(false);
                                    return;
                                }
                            }
                        }
                    } else {
                        delVehicles.push(temp[index]);
                    }
                }

                const tempPlates = [...plates];
                if (temp[index].dePlaca && temp[index].dePlaca !== '') {
                    let indexPlate = null;
                    plates.forEach((o, i) => {
                        if (o === temp[index].dePlaca) {
                            indexPlate = i;
                        }
                    });
                    if (indexPlate == null) {
                        tempPlates.push(temp[index].dePlaca);
                    }
                }
                temp.splice(index, 1);
                await dispatch(setVehicles(temp));
                await dispatch(setDeletedVehicles(delVehicles));
                setPlates(tempPlates);
                setInitialLoading(false);
            }
        }
    };

    return (
        <>
            <GenericDrawer
                open={openDrawer}
                title="Cadastro de Veículo"
                onClose={() => {
                    closeToast(toastIds.atualizacaoDETRAN);
                    onCloseDrawer();
                }}>
                <Container>
                    <VehicleForm
                        {...props}
                        classes={classes}
                        vehicle={vehicleSelected}
                        onSave={onSaveRow}
                        onCancel={onCancel}
                        vehiclesLimit={maxVehicles}
                    />
                </Container>
            </GenericDrawer>

            {(initialLoading || loadingPlates) && (
                <div className={classes.centerProgress}>
                    <CircularProgress size={20} />
                </div>
            )}

            {!initialLoading && !loadingPlates && (
                <>
                    <GrowingTable
                        title="Veículos"
                        titleTableLimit={'Número máximo de veículos nesta solicitação: '}
                        tableLimit={maxVehicles}
                        tabledata={tableData}
                        addtabledata={addElement}
                        customEditElement={customEditElement}
                        showEditButton={canShowEditButton}
                        handlechange={onChangeField}
                        customAddElement={changeVehicles}
                        isInternalForm={isInternalForm}
                        canEdit={canShowEditButton}
                        blockadd={loadingVehicleData}
                    />
                    {tableData.data.length > 0 && (
                        <div className={classes.boxCount}>
                            <strong>Veículos adicionados</strong>
                            <span>{tableData.data.length}</span>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default injectIntl(withVehicleInitialization(TableEdit));
